import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import { toast } from 'react-toastify';

import { IValidator } from 'interfaces/validator';
import { changePassword } from 'services/user';
import YM from 'utils/metrics';
import { removeTokenWithRedirect } from 'utils/token';
import { validateValue, validateForm } from 'utils/validation';
import Button from 'components/Button';
import InputPassword from 'components/Input/InputPassword';
import Modal from '../';
import classes from './modalChangePassword.module.scss';
import { IModalChangePassword } from './types';

const ERROR_SAME_PASSWORDS = 'Новый пароль должен отличаться от старого';

const ModalChangePassword: React.FC<IModalChangePassword> = ({
	className,
	isOpen,
	onClose,
}) => {
	const [passwordData, setPasswordData] = useState({
		password: '',
		confirmPassword: '',
		oldPassword: '',
	});
	const [errors, setErrors] = useState<{ [key: string]: string }>({
		password: '',
		confirmPassword: '',
		oldPassword: '',
	});
	const [isLoading, setIsLoading] = useState(false);

	const resetFormData = () => {
		const initialState = {
			password: '',
			confirmPassword: '',
			oldPassword: '',
		};
		setPasswordData(initialState);
		setErrors(initialState);
	};

	useEffect(() => {
		if (!isOpen) {
			resetFormData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const validateFieldValue = ({ type, value }: IValidator): void => {
		const isNewPassword = type === 'password';
		const isConfirmPassword = type === 'confirmPassword';
		const isOldPassword = type === 'oldPassword';
		const isSamePasswords =
			(isNewPassword && value === passwordData.oldPassword) ||
			(isOldPassword && value === passwordData.password);
		const error = validateValue({
			type,
			value,
			isRequired: true,
			confirmValue: isConfirmPassword ? passwordData.password : '',
		});
		const newErrors = { ...errors, [type]: error };
		if (
			!isConfirmPassword &&
			(!newErrors.password || newErrors.password === ERROR_SAME_PASSWORDS)
		) {
			newErrors.password = isSamePasswords ? ERROR_SAME_PASSWORDS : '';
		}
		setErrors(newErrors);
	};

	const handleChange =
		(type: string) =>
		(value: string): void => {
			setPasswordData({ ...passwordData, [type]: value });
			validateFieldValue({ type, value });
		};

	const handleSubmit = async (
		e: React.FormEvent<HTMLFormElement>
	): Promise<void> => {
		e.preventDefault();
		if (isLoading) return;

		const { isValid, formErrors } = validateForm({
			formData: passwordData,
			isRequired: true,
		});
		const isSamePasswords =
			passwordData.password === passwordData.oldPassword;
		if (!isValid || isSamePasswords) {
			return setErrors({
				...formErrors,
				password:
					formErrors.password ||
					(isSamePasswords && ERROR_SAME_PASSWORDS) ||
					'',
			});
		}

		setIsLoading(true);
		try {
			await changePassword(passwordData);
			YM.onFormSubmit();
			toast.success('Пароль успешно изменен');
			onClose();
			removeTokenWithRedirect();
		} catch (e) {
			toast.error('Не удалось изменить пароль');
		}
		setIsLoading(false);
	};

	const formInputs = (
		<div className={classes.formInputs}>
			<InputPassword
				className={classes.field}
				value={passwordData.password}
				onChange={handleChange('password')}
				placeholder="Новый пароль"
				error={errors.password}
			/>
			<InputPassword
				className={classes.field}
				value={passwordData.confirmPassword}
				onChange={handleChange('confirmPassword')}
				placeholder="Повторный ввод пароля"
				error={errors.confirmPassword}
			/>
			<InputPassword
				className={classes.field}
				value={passwordData.oldPassword}
				onChange={handleChange('oldPassword')}
				placeholder="Старый пароль"
				error={errors.oldPassword}
			/>
		</div>
	);

	const formButtons = (
		<div className={classes.buttons}>
			<Button
				className={classes.buttonSubmit}
				text="Сохранить"
				type="submit"
				isLoading={isLoading}
			/>
			<Button
				className={classes.buttonCancel}
				text="Отмена"
				styleType="secondary"
				onClick={onClose}
			/>
		</div>
	);

	const form = (
		<form className={classes.form} onSubmit={handleSubmit}>
			{formInputs}
			{formButtons}
		</form>
	);

	return (
		<Modal
			className={cn(classes.modalChangePassword, className)}
			isOpen={isOpen}
			onClose={onClose}
		>
			<div className={classes.title}>Введите ваш новый пароль</div>
			{form}
		</Modal>
	);
};

export default ModalChangePassword;
